import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article'
  publishedTime?: string
  modifiedTime?: string
  author?: string
}

const BASE_TITLE = 'شليل للخدمات اللوجستية - Shalil Logistics'
const BASE_DESC = 'خدمات لوجستية متكاملة - شحن بحري، جوي، وبري - تخليص جمركي - تخزين وتوزيع في جميع موانئ مصر'
const SITE_URL = 'https://shelil-logistics-services.pages.dev'

export default function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
}: SEOProps) {
  const ogImage = image || `${SITE_URL}/logo.png`
  const ogUrl = url || SITE_URL
  const path = (() => {
    try {
      return new URL(ogUrl).pathname || '/'
    } catch {
      return ogUrl.startsWith('/') ? ogUrl : '/'
    }
  })()
  const canonical = `${SITE_URL}${path === '/' ? '/' : path}`

  useEffect(() => {
    const fullTitle = title ? `${title} | ${BASE_TITLE}` : BASE_TITLE
    const finalDesc = description || BASE_DESC
    document.title = fullTitle

    const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }

    // Canonical link
    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    link.href = canonical

    upsertMeta('name', 'description', finalDesc)
    upsertMeta('name', 'robots', 'index, follow')
    upsertMeta('name', 'author', author || 'Shalil Logistics Services')
    upsertMeta('name', 'language', 'ar-EG')

    upsertMeta('property', 'og:title', fullTitle)
    upsertMeta('property', 'og:description', finalDesc)
    upsertMeta('property', 'og:type', type)
    upsertMeta('property', 'og:url', ogUrl)
    upsertMeta('property', 'og:image', ogImage)
    upsertMeta('property', 'og:image:alt', fullTitle)
    upsertMeta('property', 'og:site_name', 'Shalil Logistics')
    upsertMeta('property', 'og:locale', 'ar_EG')
    upsertMeta('property', 'og:locale:alternate', 'en_US')
    if (type === 'article') {
      if (publishedTime) upsertMeta('property', 'article:published_time', publishedTime)
      if (modifiedTime) upsertMeta('property', 'article:modified_time', modifiedTime)
    }

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', fullTitle)
    upsertMeta('name', 'twitter:description', finalDesc)
    upsertMeta('name', 'twitter:image', ogImage)
    upsertMeta('name', 'twitter:image:alt', fullTitle)
  }, [title, description, ogImage, ogUrl, type, publishedTime, modifiedTime, author, canonical])

  return null
}
